import React, { useState } from 'react';
import OTPVerification from './OTPVerification';
import './Auth.css';

const API_URL = 'https://nethajiprime-backend.onrender.com';

function PasswordReset({ onBack, onSuccess }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/password-reset/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send OTP');
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    setOtp(otpCode);
    setStep(3);
  };

  const handleResendOTP = async () => {
    const response = await fetch(`${API_URL}/password-reset/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to resend OTP');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/password-reset/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to reset password');
      if (onSuccess) onSuccess();
      else { alert('✅ Password reset successful!'); onBack(); }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 2) {
    return (
      <OTPVerification
        email={email}
        onVerify={handleVerifyOTP}
        onResend={handleResendOTP}
        onBack={() => setStep(1)}
        verificationType="password-reset"
      />
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">🔐</div>
          <h1>NETHAJI PRIME</h1>
          <h2>{step === 1 ? 'Reset Password' : 'New Password'}</h2>
          <p>{step === 1 ? 'Enter your email to receive a verification code' : 'Create your new password'}</p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleRequestOTP} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
              />
            </div>
            {error && <div className="error-message">❌ {error}</div>}
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? '⏳ Sending...' : '📧 Send Verification Code'}
            </button>
            <div className="forgot-password" style={{ textAlign: 'center', marginTop: '8px' }}>
              <button type="button" onClick={onBack}>← Back to Login</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="auth-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  autoFocus
                  style={{ paddingRight: '48px', width: '100%', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '16px', padding: '0' }}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  style={{ paddingRight: '48px', width: '100%', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '16px', padding: '0' }}>
                  {showConfirm ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            {/* Password strength indicator */}
            {newPassword && (
              <div style={{ display: 'flex', gap: '4px', marginTop: '-8px' }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    flex: 1, height: '3px', borderRadius: '2px',
                    background: newPassword.length >= i * 3
                      ? newPassword.length >= 10 ? '#00d480' : newPassword.length >= 7 ? '#ffa500' : '#ff4444'
                      : '#243447'
                  }} />
                ))}
                <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px' }}>
                  {newPassword.length < 6 ? 'Too short' : newPassword.length < 7 ? 'Weak' : newPassword.length < 10 ? 'Fair' : 'Strong'}
                </span>
              </div>
            )}
            {error && <div className="error-message">❌ {error}</div>}
            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? '⏳ Resetting...' : '✅ Reset Password'}
            </button>
            <div className="forgot-password" style={{ textAlign: 'center', marginTop: '8px' }}>
              <button type="button" onClick={() => setStep(1)}>← Start Over</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default PasswordReset;